import type { DeliveryConfig } from './checkout';

/**
 * Livraison multi-zones (parité C5/A4 — legacy Food Kourt v11).
 * Le tenant définit des zones `{ name, feeCents, minCents }` (colonne jsonb
 * `delivery_zones`). Le client choisit sa zone au checkout → frais & minimum
 * de cette zone. Si aucune zone n'est définie, on retombe sur la livraison
 * « simple » (un frais + un minimum). Logique PURE et testable.
 */
export interface DeliveryLocality {
  name: string;
  postalCodes: string[];
}
export interface DeliveryZone {
  name: string;
  feeCents: number;
  minCents: number;
  /** Localités couvertes (NPA + ville) — présentes en mode « détection par NPA » (parité legacy v11). */
  localities?: DeliveryLocality[];
}

function asNumber(...vals: unknown[]): number {
  for (const v of vals) if (typeof v === 'number' && Number.isFinite(v)) return v;
  return NaN;
}

/**
 * Normalise la colonne jsonb `delivery_zones` en zones sûres. Pur.
 * Supporte 2 formats : (a) liste simple `{name, fee_cents, min_cents}` et
 * (b) format legacy NPA `{label|name, min_cents, fee_cents, localities:[{name, postal_codes}]}`.
 */
export function parseDeliveryZones(raw: unknown): DeliveryZone[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((z) => {
      const o = (z ?? {}) as Record<string, unknown>;
      const name = (typeof o.name === 'string' ? o.name : typeof o.label === 'string' ? o.label : '').trim();
      const feeRaw = asNumber(o.fee_cents, o.feeCents);
      const feeCents = Number.isFinite(feeRaw) ? feeRaw : 0; // legacy : frais 0 par défaut
      const minRaw = asNumber(o.min_cents, o.minCents);
      const minCents = Number.isFinite(minRaw) ? minRaw : 0;
      const localities: DeliveryLocality[] = Array.isArray(o.localities)
        ? (o.localities as Record<string, unknown>[])
            .map((l) => {
              const lname = typeof l?.name === 'string' ? l.name.trim() : '';
              const pcs = Array.isArray(l?.postal_codes) ? l.postal_codes
                : Array.isArray((l as Record<string, unknown>)?.postalCodes) ? (l as Record<string, unknown>).postalCodes
                : [];
              return { name: lname, postalCodes: (pcs as unknown[]).map((p) => String(p).trim()).filter(Boolean) };
            })
            .filter((l) => l.name !== '' || l.postalCodes.length > 0)
        : [];
      return localities.length > 0 ? { name, feeCents, minCents, localities } : { name, feeCents, minCents };
    })
    .filter((z) => z.name !== '');
}

/** Y a-t-il une détection par NPA (zones avec localités) ? Pur. */
export function hasNpaZones(zones: DeliveryZone[]): boolean {
  return zones.some((z) => (z.localities?.length ?? 0) > 0);
}

/** Normalise une ville pour comparaison (sans accents/espaces/casse). Pur (parité legacy). */
export function normalizeCity(s: string | null | undefined): string {
  return (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s'\-/]/g, '')
    .trim();
}

/**
 * Détecte la zone d'un client par NPA + ville (parité legacy v11). Pur.
 * 1) match NPA exact ; 1 zone → elle ; plusieurs → départage par ville sinon zone au
 *    minimum le plus bas (avantage client). 2) sinon match par ville. Sinon null (hors zone).
 */
export function detectDeliveryZone(
  postal: string | null | undefined,
  city: string | null | undefined,
  zones: DeliveryZone[],
): DeliveryZone | null {
  if (!Array.isArray(zones) || zones.length === 0) return null;
  const cleanPostal = (postal ?? '').toString().trim();
  const cleanCity = normalizeCity(city);

  const flat: { locNorm: string; postal: string; zone: DeliveryZone }[] = [];
  for (const z of zones) {
    for (const loc of z.localities ?? []) {
      for (const pc of loc.postalCodes) flat.push({ locNorm: normalizeCity(loc.name), postal: pc, zone: z });
    }
  }
  if (flat.length === 0) return null;

  const npaMatches = cleanPostal ? flat.filter((f) => f.postal === cleanPostal) : [];
  if (npaMatches.length === 1) return npaMatches[0].zone;
  if (npaMatches.length > 1) {
    if (cleanCity) {
      const byCity = npaMatches.find((f) => f.locNorm === cleanCity || f.locNorm.includes(cleanCity) || cleanCity.includes(f.locNorm));
      if (byCity) return byCity.zone;
    }
    return [...npaMatches].sort((a, b) => (a.zone.minCents || 0) - (b.zone.minCents || 0))[0].zone;
  }
  if (cleanCity) {
    const byCity = flat.find((f) => f.locNorm === cleanCity);
    if (byCity) return byCity.zone;
    const fuzzy = flat.find((f) => f.locNorm.startsWith(cleanCity) || cleanCity.startsWith(f.locNorm));
    if (fuzzy) return fuzzy.zone;
  }
  return null;
}

/**
 * Config de livraison effective selon la zone choisie.
 * - Sans zones → la config simple du tenant.
 * - Avec zones → la zone sélectionnée (frais + minimum de la zone) ; tant qu'aucune
 *   zone n'est choisie, livraison « non prête » (feeCents 0, minimum infini → bloque).
 * Pur.
 */
export function effectiveDelivery(
  base: DeliveryConfig,
  zones: DeliveryZone[],
  selectedZoneName: string | null,
): DeliveryConfig {
  if (zones.length === 0) return base;
  const zone = zones.find((z) => z.name === selectedZoneName);
  if (!zone) {
    // Zone non choisie : on bloque la validation (minimum inatteignable).
    return { enabled: true, feeCents: 0, minCents: Number.MAX_SAFE_INTEGER };
  }
  return { enabled: true, feeCents: zone.feeCents, minCents: zone.minCents };
}
